/**
 * API client for interacting with the backend
 */

// Set API base URL explicitly to the production URL
const API_BASE_URL = 'https://voice-agent-api-248940949358.us-central1.run.app';

/**
 * Make API request with fetch
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Default options
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  
  // Merge options
  const fetchOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };
  
  try {
    const response = await fetch(url, fetchOptions);
    
    // Parse JSON response
    const data = await response.json();
    
    // Handle API errors
    if (!response.ok) {
      throw new Error(data.detail || 'API request failed');
    }
    
    return data;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
}

// Client API
export const clientsApi = {
  /**
   * Get all clients
   */
  getClients: async () => {
    return apiRequest('/clients/');
  },
  
  /**
   * Create a new client
   */
  createClient: async (clientData) => {
    return apiRequest('/clients/', {
      method: 'POST',
      body: JSON.stringify(clientData),
    });
  },
  
  /**
   * Delete a client
   */
  deleteClient: async (clientId) => {
    return apiRequest(`/clients/${clientId}`, {
      method: 'DELETE',
    });
  },
  
  /**
   * Regenerate API key for a client
   */
  regenerateApiKey: async (clientId) => {
    return apiRequest(`/clients/${clientId}/regenerate-api-key`, {
      method: 'POST',
    });
  },
};

// Agent API
export const agentsApi = {
  /**
   * Get all agents
   */
  getAgents: async (clientId = null) => {
    const query = clientId ? `?client_id=${clientId}` : '';
    return apiRequest(`/agents/${query}`);
  },
  
  /**
   * Get agent by ID
   */
  getAgent: async (agentId) => {
    return apiRequest(`/agents/${agentId}`);
  },
  
  /**
   * Create a new agent
   */
  createAgent: async (agentData) => {
    // First, we need to find or create the client based on client_name
    let clientId;
    const clientName = agentData.client_name;
    
    try {
      // Check if client exists
      const clientsResponse = await clientsApi.getClients();
      const existingClient = clientsResponse.clients.find(
        client => client.name.toLowerCase() === clientName.toLowerCase()
      );
      
      // If client exists, use its ID
      if (existingClient) {
        clientId = existingClient.id;
      } else {
        // Otherwise, create new client
        const newClient = await clientsApi.createClient({
          name: clientName
        });
        clientId = newClient.id;
      }
      
      // Replace client_name with client_id and add fixed webhook URL
      const formattedData = {
        ...agentData,
        client_id: clientId,
        webhook_url: "https://us-central1-vittae360-prod.cloudfunctions.net/webhook-handler-autoagent"
      };
      
      // Remove client_name as it's not needed by the API
      delete formattedData.client_name;
      
      // Send request to create agent
      return apiRequest('/agents/', {
        method: 'POST',
        body: JSON.stringify(formattedData),
      });
    } catch (error) {
      console.error('Error creating agent:', error);
      throw error;
    }
  },
  
  /**
   * Update an agent
   */
  updateAgent: async (agentId, agentData) => {
    return apiRequest(`/agents/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify(agentData),
    });
  },
  
  /**
   * Delete an agent
   */
  deleteAgent: async (agentId) => {
    return apiRequest(`/agents/${agentId}`, {
      method: 'DELETE',
    });
  },
};

// Data API
export const dataApi = {
  /**
   * Get extracted data by agent
   */
  getDataByAgent: async (agentId) => {
    return apiRequest(`/data/extracted-data/by-agent/${agentId}`);
  },
  
  /**
   * Get extracted data by user
   */
  getDataByUser: async (userId) => {
    return apiRequest(`/data/extracted-data/by-user/${userId}`);
  },
  
  /**
   * Get extracted data by conversation
   */
  getDataByConversation: async (conversationId) => {
    return apiRequest(`/data/extracted-data/by-conversation/${conversationId}`);
  },
  
  /**
   * Get all users
   */
  getUsers: async (clientId = null) => {
    const query = clientId ? `?client_id=${clientId}` : '';
    return apiRequest(`/data/users${query}`);
  },
  
  /**
   * Get conversations
   */
  getConversations: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    
    if (filters.agentId) {
      queryParams.append('agent_id', filters.agentId);
    }
    
    if (filters.userId) {
      queryParams.append('user_id', filters.userId);
    }
    
    if (filters.status) {
      queryParams.append('status', filters.status);
    }
    
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return apiRequest(`/data/conversations${query}`);
  },
};
